/**
 * Where in the Markdown did the person click?
 *
 * The rendered note and its source differ only in markup, so the text under
 * the pointer is almost always a verbatim substring of the source. Take the
 * text node the click landed in, find it in the source, and add the offset
 * inside it — the caret then opens on the sentence being read instead of at
 * the top of a document that must be scrolled to find it again.
 *
 * Ambiguity is resolved by neighbourhood: when the same words appear twice,
 * the occurrence whose surrounding block text also matches wins. When nothing
 * matches at all (an image, a rendered link title that is not in the source),
 * the answer is `null` and the editor opens as it always did.
 */
export function sourceOffsetFromClick(
  event: { clientX: number; clientY: number; target?: EventTarget | null },
  source: string,
): number | null {
  const exact = fromCaretApis(event, source);
  if (exact !== null) return exact;
  // No caret API (some embedded browsers), or the hit was not a text node:
  // fall back to the block that was clicked. "The paragraph you were reading"
  // is the promise; the exact character is a refinement.
  return fromClickedBlock(event.target, source);
}

function fromCaretApis(
  event: { clientX: number; clientY: number },
  source: string,
): number | null {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  let node: Node | null = null;
  let offset = 0;
  if (doc.caretPositionFromPoint) {
    const hit = doc.caretPositionFromPoint(event.clientX, event.clientY);
    if (hit) [node, offset] = [hit.offsetNode, hit.offset];
  } else if (doc.caretRangeFromPoint) {
    const range = doc.caretRangeFromPoint(event.clientX, event.clientY);
    if (range) [node, offset] = [range.startContainer, range.startOffset];
  }
  // An element hit (the caret landed between children): descend to the text
  // node at that offset, or the first one, rather than giving up.
  if (node && node.nodeType !== Node.TEXT_NODE) {
    const child = node.childNodes[Math.min(offset, node.childNodes.length - 1)] ?? null;
    const walker = document.createTreeWalker(child ?? node, NodeFilter.SHOW_TEXT);
    node = walker.nextNode();
    offset = 0;
  }
  if (!node || node.nodeType !== Node.TEXT_NODE) return null;

  const text = node.textContent ?? '';
  const needle = text.trim();
  if (needle.length < 2) return null;
  const lead = text.length - text.trimStart().length;
  const within = Math.max(0, Math.min(offset - lead, needle.length));

  const blockText =
    node.parentElement?.closest(BLOCKS)?.textContent?.trim() ?? '';
  const at = locate(source, needle, blockText);
  return at === null ? null : at + within;
}

const BLOCKS = 'li, p, h1, h2, h3, h4, h5, h6, blockquote, td, pre';

/** The start, in the source, of the block element that was clicked. */
function fromClickedBlock(target: EventTarget | null | undefined, source: string): number | null {
  if (!(target instanceof Element)) return null;
  const block = target.closest(BLOCKS) ?? target;
  // The block's own text, minus what nested blocks contribute: a list item
  // that contains a sub-list must be found by its first line, not by the
  // whole subtree.
  const own = [...block.childNodes]
    .filter((n) => n.nodeType === Node.TEXT_NODE || !(n instanceof Element && n.matches(BLOCKS + ', ul, ol')))
    .map((n) => n.textContent ?? '')
    .join('')
    .trim();
  const needle = own.slice(0, 80).trim();
  if (needle.length < 2) return null;
  // Rendered text may have lost inline markup; try the longest prefix that
  // still occurs in the source before giving up.
  for (let length = needle.length; length >= 8; length = Math.floor(length * 0.6)) {
    const at = locate(source, needle.slice(0, length).trim(), block.textContent?.trim() ?? '');
    if (at !== null) return at;
  }
  return null;
}

/** All occurrences of `needle`; several are disambiguated by nearby words. */
function locate(source: string, needle: string, context: string): number | null {
  const candidates: number[] = [];
  for (let at = source.indexOf(needle); at >= 0; at = source.indexOf(needle, at + 1)) {
    candidates.push(at);
  }
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0] ?? 0;
  const words = context.split(/\s+/).filter((w) => w.length > 3).slice(0, 8);
  let best = candidates[0] ?? 0;
  let bestScore = -1;
  for (const at of candidates) {
    const around = source.slice(Math.max(0, at - 200), at + needle.length + 200);
    const score = words.reduce((n, w) => n + (around.includes(w) ? 1 : 0), 0);
    if (score > bestScore) {
      best = at;
      bestScore = score;
    }
  }
  return best;
}
